import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import type { UserFriendlyError } from '../../lib/errorMessages';
import { getUserFriendlyError } from '../../lib/errorMessages';

interface ErrorDisplayProps {
  error?: unknown;
  customError?: UserFriendlyError;
  onAction?: () => void;
  compact?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: 12,
  border: `1px solid ${theme.palette.error.light}`,
}));

const CompactContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.error.light,
  borderRadius: 8,
  border: `1px solid ${theme.palette.error.main}`,
}));

const IconContainer = styled(Box)(({ theme }) => ({
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
}));

const Title = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Mono", monospace',
  fontWeight: 600,
  fontSize: '1.25rem',
  color: theme.palette.error.main,
  marginBottom: theme.spacing(1),
}));

const Message = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.9375rem',
  color: theme.palette.text.secondary,
  maxWidth: 400,
  marginBottom: theme.spacing(3),
}));

const CompactMessage = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  color: theme.palette.error.dark,
  flex: 1,
}));

export function ErrorDisplay({ error, customError, onAction, compact = false }: ErrorDisplayProps) {
  const errorInfo = customError || getUserFriendlyError(error);

  if (compact) {
    return (
      <CompactContainer>
        <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 24 }} />
        <CompactMessage>
          <strong>{errorInfo.title}:</strong> {errorInfo.message}
        </CompactMessage>
        {errorInfo.action && onAction && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={onAction}
          >
            {errorInfo.action}
          </Button>
        )}
      </CompactContainer>
    );
  }

  return (
    <Container>
      <IconContainer>
        <ErrorOutlineIcon sx={{ fontSize: 64 }} />
      </IconContainer>
      <Title>{errorInfo.title}</Title>
      <Message>{errorInfo.message}</Message>
      {errorInfo.action && onAction && (
        <Button
          variant="contained"
          color="error"
          onClick={onAction}
        >
          {errorInfo.action}
        </Button>
      )}
    </Container>
  );
}
